import {
  ComponentService,
  DataTransmissionService,
  DbCommonColumnName,
  DevelopScriptServicePack,
  EditType,
  EditTypeUtility,
  EnvironmentType,
  GocooEngineConfig,
  LoginUserUtility,
  MessageService,
  ModelUtility,
  ResponseDataModel,
  SendEMailAttachmentFile,
  SendEMailRequestDataFormat,
  ShowDefaultDialogService,
  ValidateCheckUtility,
  ViewType,
} from '@ktvs8/gocoo-engine-front-end';
import { ApplicationEnvironmentTypeUtility } from '../develop/enum/application-environment-type';
import { MProgramUtility } from './m-program-utility';
import { MSupplierUtility } from './m-supplier-utility';

/**
 * E-Mailを送信する際のユーティリティクラス
 */
export namespace EMailUtility {
  /**
   * 認証キーを生成する
   * @returns
   */
  export function getVerificationKey(): string {
    const defaultCharset = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnopqrstuvwxyz0123456789';
    let verificationKey = '';
    const randomArray = new Uint8Array(28);
    crypto.getRandomValues(randomArray);
    randomArray.forEach((randomByte) => {
      verificationKey += defaultCharset[randomByte % defaultCharset.length];
    });

    return verificationKey;
  }

  /**
   * 発注書発行処理のメール送信を実施する
   *
   * ## 処理概要
   * 1. メール送信に必要なインターフェースを作成
   * 2. メール送信を実施
   *
   * @param registerRecord 登録したレコード
   * @param mProgramTicketKey 関連する番組マスタのチケットキー
   * @param mSupplierTicketKey 関連する発注先マスタのチケットキー
   * @param fileName 添付するファイル名
   * @param service 開発用のサービス
   * @returns 成功した場合はtrue 失敗した場合はfalse
   */
  export async function sendOrderSheetEmail(
    registerRecord: any,
    mProgramTicketKey: string,
    mSupplierTicketKey: string,
    fileName: string,
    service: DevelopScriptServicePack
  ): Promise<boolean> {
    const sendEMailRequestDataFormat = await createSendEMailRequestDataFormat(
      registerRecord,
      mProgramTicketKey,
      mSupplierTicketKey,
      fileName
    );

    // メールを送信
    return new Promise((resolve, reject) => {
      // メール送信可能な環境かチェック
      if (!canSendEmailByEnvironment()) {
        resolve(true);
        return;
      }

      const callBack = (response: ResponseDataModel) => {
        if (!response.isCompleted) {
          reject(false);
        }
        resolve(response.data);
      };
      service.dataTransmissionService.sendEMail(sendEMailRequestDataFormat, callBack, null).then((response) => {
        if (response == null) {
          reject(false);
        }
        resolve(true);
      });
    });
  }
  /**
   * メール送信のリクエストモデルを作成
   * @returns
   */
  export function createSendEMailRequestDataFormat(
    registerRecord: any,
    mProgramTicketKey: string,
    mSupplierTicketKey: string,
    fileName: string
  ): Promise<SendEMailRequestDataFormat> {
    return new Promise(async (resolve, reject) => {
      // 送信先のメールアドレスを取得
      const fetchSendInfo = getOrderSheetSendMailInfo(mSupplierTicketKey);

      // CCメールアドレスリストを取得
      const fetchCCList = getOrderSheetCCMailAddressList(mProgramTicketKey);

      //ハラスメント窓口を取得
      const fetchHarassment = getHarassmentHotline();

      Promise.all([fetchSendInfo, fetchCCList, fetchHarassment])
        .then((results) => {
          const sendMailInfo = results[0];
          const ccMailAddressList = results[1];
          const harassmentHotline = results[2];

          // メール送信するリクエストモデルを作成
          const sendEMailRequestDataFormat: SendEMailRequestDataFormat = {
            toMailAddressList: [sendMailInfo.EmailAddress],
            ccMailAddressList: ccMailAddressList,
            bccMailAddressList: [],
            fromMailAddress: ApplicationEnvironmentTypeUtility.getSystemFromEmailAdress(),
            subject: getSubject(registerRecord),
            body: getBody(registerRecord, sendMailInfo, harassmentHotline),
            attachmentFileList: [
              <SendEMailAttachmentFile>{
                isPutOnOutputDirectory: true,
                bucketName: GocooEngineConfig.environment.systemName,
                key: '',
                sendFileName: fileName,
              },
            ],
            eMailType: 'ordersheet',
            bounceEMailAddress: GocooEngineConfig.loginUser.mailAddress,
          };

          resolve(sendEMailRequestDataFormat);
        })
        .catch((error) => {
          reject(error);
        });
    });
  }

  /**
   * 発注書発行を実施する際のCCメールアドレスリストを取得する
   * @param mProgramTicketKey
   * @returns
   */
  export function getOrderSheetCCMailAddressList(mProgramTicketKey: string): Promise<string[]> {
    let ccMailAddressList: string[] = [];
    return new Promise((resolve, reject) => {
      // 発注書を発行したユーザー
      ccMailAddressList.push(GocooEngineConfig.loginUser.mailAddress);

      // 紐づく番組のプロデューサー
      const fetchProducerEMailList = MProgramUtility.fetchProducerEMailAddress(mProgramTicketKey);

      // ※一旦、管理ユーザーを発注書送信先から除外しておく（いずれ個人ごとの送信設定をできるようにしたい）※

      // 管理ユーザー
      // const fetchManagerList = ApplicationUserUtility.fetchManagerList();

      // Promise.all([fetchProducerEMailList, fetchManagerList])
      Promise.all([fetchProducerEMailList])
        .then((result) => {
          const producerEMailList = result[0];
          // const managerList = result[1];

          // 紐づくプロデューサー
          producerEMailList.forEach((mailAddress) => {
            ccMailAddressList.push(mailAddress);
          });

          // 管理ユーザー
          // managerList.forEach((mailAddress: any) => {
          //   ccMailAddressList.push(mailAddress['Email']);
          // });

          // 追加送信先（環境変数で設定している）
          const ordersheetExtraDestinations = ApplicationEnvironmentTypeUtility.getOrdersheetExtraDestinations();
          ordersheetExtraDestinations.forEach((mailAddress) => {
            ccMailAddressList.push(mailAddress);
          });

          // 重複を削除
          ccMailAddressList = ccMailAddressList.filter((x, i, self) => self.indexOf(x) === i);

          resolve(ccMailAddressList);
        })
        .catch((error) => {
          throw error;
        });
    });
  }

  /**
   * ハラスメント窓口の連絡先を返す。
   * @returns
   */
  export function getHarassmentHotline(): Promise<string> {
    const componentService = new ComponentService(new ShowDefaultDialogService(), new MessageService());
    const dtService = new DataTransmissionService(componentService);

    return dtService
      .fetchRecord(
        ViewType.GroupEdit,
        'Settings',
        {
          [DbCommonColumnName.TICKET_TYPE]: 'Settings',
          [DbCommonColumnName.TICKET_KEY]: 'HarassmentHotline',
        },
        null
      )
      .then((result) => (result.data['Value'] ? result.data['Value'] : ''));
  }

  /**
   * 送信先のメールアドレスを取得する
   * @param mSupplierTicketKey 発注先のチケットキー
   */
  export function getOrderSheetSendMailInfo(mSupplierTicketKey: string): Promise<SendEMailInfo> {
    return new Promise(async (resolve, reject) => {
      const mSupplier = await MSupplierUtility.getMSupplier(mSupplierTicketKey);
      if (ValidateCheckUtility.isEmpty(mSupplier)) {
        reject('発注先が取得できませんでした。');
      }

      const SendEMail = <SendEMailInfo>{
        EmailAddress: mSupplier['EmailAddress'],
        SupplierName: mSupplier['SupplierName'],
        Person: mSupplier['Person'],
      };
      resolve(SendEMail);
    });
  }

  /**
   * メール送信先情報
   */
  type SendEMailInfo = {
    EmailAddress: string;
    SupplierName: string;
    Person: string;
  };
  /**
   * メール送信が可能な環境かチェック
   * @returns
   */
  export function canSendEmailByEnvironment(): boolean {
    return !canNotSenEMailEnvironmentList.includes(GocooEngineConfig.environment.environmentType);
  }

  /**
   * 実際のメール送信を行わない環境リスト
   */
  const canNotSenEMailEnvironmentList = [
    EnvironmentType.UseMock,
    EnvironmentType.DevelopmentBackendServer,
    EnvironmentType.StationDev,
  ];

  /**
   * 件名を取得する
   *
   * {番組名}{発注内容}発注書{委託先名}様{放送日※1}
   * @param registerRecord
   * @returns
   */
  function getSubject(registerRecord: any): string {
    let program = '';
    if (registerRecord['MProgramProgramName']) {
      program = registerRecord['MProgramProgramName'];
    } else {
      program = registerRecord['ProgramName'] ?? '';
    }
    const orderContent = registerRecord['OrderContent'] ?? '';
    const supplierName = registerRecord['SupplierName'] ?? '';
    const onAirDates = ModelUtility.getValueInObjectAsArray(registerRecord, 'OnairDates');

    let subject =
      orderContent === '' ? `${program}発注書 ${supplierName}様` : `${program} ${orderContent}発注書 ${supplierName}様`;
    if (onAirDates.length === 0) {
    } else if (onAirDates.length === 1) {
      subject += ` ${onAirDates[0]}`;
    } else {
      subject += ` ${onAirDates.sort()[0]} - ${onAirDates.sort()[onAirDates.length - 1]}`;
    }
    if (EditTypeUtility.getEditTypeInUrl() === EditType.Edit) {
      return '[再発行]' + subject;
    } else {
      return subject;
    }
  }

  /**
   * メール本文を取得
   *
   * @param registerRecord
   */
  function getBody(registerRecord: any, sendEMailInfo: SendEMailInfo, harassmentHotline: string): string {
    /**
     * メールのフォーマットの置き換えを実施
     * @param body
     * @param key
     * @returns
     */
    const replaceToRegisterRecord = (body: string, key: string) => {
      const value = registerRecord[key] ?? '';
      return body.replace('${' + key + '}', value);
    };

    /**
     * メール本文の放送日を取得
     * @returns
     */
    const getOnAirDates = () => {
      const onAirDates = ModelUtility.getValueInObjectAsArray(registerRecord, 'OnairDates');
      if (onAirDates.length === 0) {
        return '';
      }
      // 昇順でソートして、「、」で連結した文字列を返す
      return onAirDates.sort().join('、');
    };

    let body = EditTypeUtility.getEditTypeInUrl() === EditType.Edit ? BODY_REISSUE_FORMAT : BODY_ISSUE_FORMAT;
    body = replaceToRegisterRecord(body, 'SupplierName');
    body = replaceToRegisterRecord(body, 'ProgramName');
    body = replaceToRegisterRecord(body, 'OrderContent');
    body = replaceToRegisterRecord(body, 'OrderDate');
    body = replaceToRegisterRecord(body, 'OrdererCompany');
    body = replaceToRegisterRecord(body, 'OrdererDept');
    body = replaceToRegisterRecord(body, 'OrdererName');
    body = replaceToRegisterRecord(body, 'AddMailMessage');
    body = replaceToRegisterRecord(body, 'url');

    body = body.replace('${Person}', sendEMailInfo.Person);

    body = body.replace('${OnairDates}', getOnAirDates());

    body = body.replace('${MailAdress}', LoginUserUtility.getLoginUserMailAddress());

    body = body.replace('${HarassmentHotline}', harassmentHotline);
    return body;
  }

  /**
   *発注先をマスタ登録時に、登録したメールアドレスに確認メールを送信する。
   * @param
   */
  export function sendVerifiedEmail(
    service: DevelopScriptServicePack,
    mSupplierTicketKey: string,
    registerRecord: any
  ): Promise<any> {
    return new Promise((resolve) => {
      /**
       * メールのフォーマットの置き換えを実施
       * @param body
       * @param key
       * @returns
       */
      const replaceToRegisterRecord = (body: string, key: string) => {
        const value = registerRecord[key] ?? '';
        return body.replace('${' + key + '}', value);
      };

      const getBodyFormat = () => {
        let body = BODY_VERIFY_FORMAT;

        body = replaceToRegisterRecord(body, 'SupplierName');
        body = replaceToRegisterRecord(body, 'Person');
        body = replaceToRegisterRecord(body, 'VerifyExpiration');
        body = replaceToRegisterRecord(body, 'VerifyRequestUser');

        body = body.replace('${url}', url);
        body = body.replace('${UserName}', GocooEngineConfig.loginUser.userName);
        body = body.replace('${MailAdress}', GocooEngineConfig.loginUser.mailAddress);

        return body;
      };
      const url = `${ApplicationEnvironmentTypeUtility.getMSupplierVerificationUrl()}/${mSupplierTicketKey}:${
        registerRecord['VerificationKey']
      }`;

      const sendEMailRequestDataFormat: SendEMailRequestDataFormat = {
        toMailAddressList: [registerRecord['EmailAddress']],
        fromMailAddress: ApplicationEnvironmentTypeUtility.getSystemFromEmailAdress(),
        subject: '関西テレビ放送からの発注書送信のご連絡',
        body: getBodyFormat(),
        ccMailAddressList: [],
        bccMailAddressList: [],
        attachmentFileList: [],
        eMailType: 'supplier_approval',
        bounceEMailAddress: GocooEngineConfig.loginUser.mailAddress,
      };

      if (!canSendEmailByEnvironment()) {
        return resolve(true);
      }

      service.dataTransmissionService
        .sendEMail(sendEMailRequestDataFormat, null, null)
        .then((response) => resolve(response));
    });
  }

  const BODY_Harassment_INFO =
    '\n' +
    '=====================================\n' +
    '＜ ハラスメント相談窓口について ＞\n' +
    '\n' +
    '弊社では、取引先の皆様が安心して業務に従事できる環境を整えるため、ハラスメント防止に努めております。\n' +
    'その一環として、以下の通りハラスメントに関するご相談やご報告を受け付ける窓口を設置しております。\n' +
    'ハラスメントやそのおそれがある事案について、ご相談が可能です。\n' +
    '\n' +
    '・ご相談内容に基づき、事実関係を調査し、必要な場合は是正処置を実施し、再発防止を図ります。\n' +
    '・ご相談いただいたことにより取引上の不利益を受けることはありません。\n' +
    '・お名前やプライバシーなどの守秘すべき事項については、秘密厳守いたします。\n' +
    '\n' +
    '${HarassmentHotline}\n' +
    '\n' +
    '=====================================\n';

  /**
   * メール本文のテンプレート
   */
  const BODY_ISSUE_FORMAT =
    '${SupplierName} ' +
    '${Person} 様 ' +
    '\n\n' +
    'お世話になっております。 ' +
    '\n' +
    '下記の番組ついての発注書を送らせていただきます。 ' +
    '\n\n' +
    '発注日：${OrderDate} ' +
    '\n' +
    '番組名：${ProgramName} ' +
    '\n' +
    '発注内容：${OrderContent} ' +
    '\n' +
    '放送日：${OnairDates} ' +
    '\n' +
    '委託者：${OrdererCompany} ' +
    '\n' +
    '発注責任者：${OrdererDept} ${OrdererName} <${MailAdress}>' +
    '\n' +
    '${AddMailMessage}' +
    '\n' +
    '内容をご確認いただき問題がないようでしたら、下記URLから受領連絡をしていただけます。' +
    '\n\n' +
    '${url}' +
    '\n\n' +
    '発行内容に不備等がございましたら、発注責任者にお問い合わせください。' +
    '\n' +
    'よろしくお願いします。' +
    '\n' +
    BODY_Harassment_INFO +
    '\n' +
    '※本メールはシステムにより自動送信しています。 ' +
    '\n\n' +
    '<< 発注書管理システム「ORDERSHEET issue」>>';

  const BODY_REISSUE_FORMAT =
    '${SupplierName} ' +
    '${Person} 様 ' +
    '\n\n' +
    'お世話になっております。 ' +
    '\n' +
    '先日発行致しました下記番組の発注書について、発注内容に変更が生じたため発注書の再発行をさせていただきます。 ' +
    '\n\n' +
    '発注日：${OrderDate} ' +
    '\n' +
    '番組名：${ProgramName} ' +
    '\n' +
    '発注内容：${OrderContent} ' +
    '\n' +
    '放送日：${OnairDates} ' +
    '\n' +
    '委託者：${OrdererCompany} ' +
    '\n' +
    '発注責任者：${OrdererDept} ${OrdererName} <${MailAdress}>' +
    '\n' +
    '${AddMailMessage}' +
    '\n' +
    '内容をご確認いただき問題がないようでしたら、下記URLから受領連絡をしていただけます。' +
    '\n\n' +
    '${url}' +
    '\n\n' +
    '発行内容に不備等がございましたら、発注責任者にお問い合わせください。' +
    '\n' +
    'よろしくお願いします。' +
    '\n' +
    BODY_Harassment_INFO +
    '\n' +
    '※本メールはシステムにより自動送信しています。 ' +
    '\n\n' +
    '<< 発注書管理システム「ORDERSHEET issue」>>';

  const BODY_VERIFY_FORMAT =
    '${SupplierName} ' +
    '${Person} 様 ' +
    '\n\n' +
    'お世話になっております。\n' +
    '関西テレビ放送では番組制作についての発注書を下記システムより発行致します。\n' +
    '\n' +
    '<< 発注書管理システム「ORDERSHEET issue」>>\n' +
    '\n' +
    '今後、こちらメールアドレス宛に発注書をお送りさせていただきます。\n' +
    '問題ないようでしたら、お手数ですが下記URLから発注書発行先メールアドレスの認証をお願い致します。\n' +
    '\n' +
    '${url}\n' +
    '\n' +
    '認証確認依頼者：${UserName} <${MailAdress}>\n' +
    '\n' +
    '※このURLの有効期限は${VerifyExpiration}です。\n' +
    '\n' +
    '※本メールが迷惑メールに分類されてしまった場合、お手数ではございますが受信トレイに移動していただき、受信許可をしていただくようお願い致します。\n' +
    '\n' +
    '※本メールはシステムにより送信しています。\n' +
    '\n' +
    '<< 発注書管理システム「ORDERSHEET issue」>> ';
}
